import React, { useState, useEffect, useRef, useCallback } from 'react';
import { quickEntryApi, matchesApi, sessionsApi, matchTxApi, sessionTxApi } from '../../lib/api';
import { GroupBox } from '../shared/GroupBox';
import { DenseTable } from '../shared/DenseTable';
import { StatusBadge } from '../shared/StatusBadge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Badge } from '../ui/badge';
import { 
  RefreshCw, Zap, Plus, Trash2, Send, Check, 
  User, Clock, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

export function QuickEntryPage() {
  const [matches, setMatches] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [recentClients, setRecentClients] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState('');
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [matchesData, sessionsData, recentData] = await Promise.all([
        matchesApi.list('OPEN'),
        sessionsApi.list(null, 'OPEN'),
        quickEntryApi.recentClients(15)
      ]);
      setMatches(matchesData);
      setSessions(sessionsData);
      setRecentClients(recentData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const addEntry = (type = 'MATCH') => {
    const newEntry = {
      id: Date.now(),
      type,
      match_id: selectedMatch,
      session_id: '',
      client_id: '',
      client_name: '',
      rate: '',
      amount: '',
      mode: type === 'MATCH' ? 'BACK' : 'YES',
      team: '',
      run: ''
    };
    setEntries([...entries, newEntry]);
  };

  const updateEntry = (id, field, value) => {
    setEntries(entries.map(e => 
      e.id === id ? { ...e, [field]: value } : e
    ));
  };

  const removeEntry = (id) => {
    setEntries(entries.filter(e => e.id !== id));
  };

  const selectClient = (entryId, client) => {
    setEntries(entries.map(e => 
      e.id === entryId ? { 
        ...e, 
        client_id: client.client_id,
        client_name: client.name || client.nick_name
      } : e
    ));
  };

  const prefillFromLast = async (entryId, clientId) => {
    try {
      const lastTxn = await quickEntryApi.lastTransaction(clientId, selectedMatch);
      const entry = entries.find(e => e.id === entryId);
      
      if (entry?.type === 'MATCH' && lastTxn.last_match_txn) {
        const last = lastTxn.last_match_txn;
        updateEntry(entryId, 'rate', last.rate);
        updateEntry(entryId, 'mode', last.mode);
        updateEntry(entryId, 'team', last.team);
        toast.success('Pre-filled from last transaction');
      } else if (entry?.type === 'SESSION' && lastTxn.last_session_txn) {
        const last = lastTxn.last_session_txn;
        updateEntry(entryId, 'rate', last.rate);
        updateEntry(entryId, 'mode', last.mode);
        updateEntry(entryId, 'run', last.run);
        toast.success('Pre-filled from last transaction');
      }
    } catch (error) {
      console.error('Failed to prefill:', error);
    }
  };

  const submitAll = async () => {
    if (entries.length === 0) {
      toast.error('No entries to submit');
      return;
    }

    // Validate entries
    const validEntries = entries.filter(e => {
      if (!e.client_id || !e.rate || !e.amount) return false;
      if (e.type === 'MATCH' && !e.team) return false;
      if (e.type === 'SESSION' && (!e.session_id || !e.run)) return false;
      return true;
    });

    if (validEntries.length === 0) {
      toast.error('No valid entries to submit');
      return;
    }

    setSubmitting(true);
    try {
      const transactions = validEntries.map(e => ({
        type: e.type,
        match_id: e.match_id,
        session_id: e.session_id,
        client_id: e.client_id,
        rate: parseFloat(e.rate),
        amount: parseFloat(e.amount),
        mode: e.mode,
        team: e.team,
        run: e.run ? parseInt(e.run) : undefined
      }));

      const result = await quickEntryApi.batch(transactions);
      
      if (result.success_count > 0) {
        toast.success(`${result.success_count} transactions created`);
        // Remove successful entries
        const successIds = validEntries.map(e => e.id);
        setEntries(entries.filter(e => !successIds.includes(e.id)));
        fetchData(); // Refresh recent clients
      }
      
      if (result.error_count > 0) {
        toast.error(`${result.error_count} transactions failed`);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedMatchData = matches.find(m => m.match_id === selectedMatch);
  const filteredSessions = sessions.filter(s => s.match_id === selectedMatch);

  return (
    <div className="space-y-4" data-testid="quick-entry-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
          <Zap className="h-6 w-6 text-primary" />
          Quick Entry
        </h1>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="font-mono">
            {entries.length} entries
          </Badge>
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-quick">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Match Selection */}
      <GroupBox label="Active Match">
        <Select value={selectedMatch} onValueChange={setSelectedMatch}>
          <SelectTrigger className="h-8" data-testid="quick-select-match">
            <SelectValue placeholder="Select an active match" />
          </SelectTrigger>
          <SelectContent>
            {matches.map(m => (
              <SelectItem key={m.match_id} value={m.match_id}>
                {m.name} - {m.team_a} vs {m.team_b}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </GroupBox>

      {selectedMatch && (
        <>
          {/* Recent Clients */}
          <GroupBox label="Recent Clients (Click to add entry)">
            <div className="flex flex-wrap gap-2">
              {recentClients.map(client => (
                <Button
                  key={client.client_id}
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => {
                    addEntry('MATCH');
                    setTimeout(() => {
                      const lastEntry = entries[entries.length];
                      if (lastEntry) {
                        selectClient(lastEntry.id, client);
                      }
                    }, 0);
                    // Add entry and select client
                    const newEntry = {
                      id: Date.now(),
                      type: 'MATCH',
                      match_id: selectedMatch,
                      session_id: '',
                      client_id: client.client_id,
                      client_name: client.name,
                      rate: '',
                      amount: '',
                      mode: 'BACK',
                      team: '',
                      run: ''
                    };
                    setEntries(prev => [...prev, newEntry]);
                  }}
                  data-testid={`quick-client-${client.client_id}`}
                >
                  <User className="h-3 w-3 mr-1" />
                  {client.nick_name || client.name}
                  {client.patti_enabled && (
                    <span className="ml-1 text-xs text-muted-foreground">
                      ({client.patti_percent}%)
                    </span>
                  )}
                </Button>
              ))}
            </div>
          </GroupBox>

          {/* Entry Actions */}
          <div className="flex gap-2">
            <Button size="sm" onClick={() => addEntry('MATCH')} data-testid="add-match-entry">
              <Plus className="h-4 w-4 mr-1" />
              Match Entry
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => addEntry('SESSION')}
              disabled={filteredSessions.length === 0}
              data-testid="add-session-entry"
            >
              <Plus className="h-4 w-4 mr-1" />
              Session Entry
            </Button>
          </div>

          {/* Entries List */}
          {entries.length > 0 && (
            <GroupBox label="Entries Queue">
              <div className="space-y-3">
                {entries.map((entry, idx) => (
                  <QuickEntryRow
                    key={entry.id}
                    entry={entry}
                    index={idx}
                    match={selectedMatchData}
                    sessions={filteredSessions}
                    recentClients={recentClients}
                    onUpdate={updateEntry}
                    onRemove={removeEntry}
                    onSelectClient={selectClient}
                    onPrefill={prefillFromLast}
                  />
                ))}
              </div>

              {/* Submit All */}
              <div className="mt-4 flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setEntries([])}
                  data-testid="clear-all-entries"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Clear All
                </Button>
                <Button 
                  onClick={submitAll}
                  disabled={submitting || entries.length === 0}
                  data-testid="submit-all-entries"
                >
                  <Send className="h-4 w-4 mr-1" />
                  {submitting ? 'Submitting...' : `Submit All (${entries.length})`}
                </Button>
              </div>
            </GroupBox>
          )}
        </>
      )}
    </div>
  );
}

function QuickEntryRow({ 
  entry, 
  index, 
  match, 
  sessions, 
  recentClients,
  onUpdate, 
  onRemove, 
  onSelectClient,
  onPrefill 
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length >= 1) {
      try {
        const results = await quickEntryApi.searchClients(q, 5);
        setSearchResults(results);
      } catch (error) {
        console.error('Search failed:', error);
      }
    } else {
      setSearchResults([]);
    }
  };

  return (
    <div className={cn(
      "p-3 border rounded-sm bg-muted/20",
      entry.type === 'MATCH' ? 'border-blue-500/30' : 'border-emerald-500/30'
    )}>
      <div className="flex items-center gap-2 mb-2">
        <Badge variant={entry.type === 'MATCH' ? 'default' : 'secondary'}>
          {entry.type}
        </Badge>
        <span className="text-xs text-muted-foreground">#{index + 1}</span>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-6 w-6 ml-auto"
          onClick={() => onRemove(entry.id)}
          data-testid={`remove-entry-${entry.id}`}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        {/* Client Search */}
        <div className="col-span-2">
          <Popover open={searchOpen} onOpenChange={setSearchOpen}>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                className="h-8 w-full justify-between"
                data-testid={`client-search-${entry.id}`}
              >
                {entry.client_name || 'Select client...'}
                <ChevronDown className="h-3 w-3 ml-2" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0">
              <Command>
                <CommandInput 
                  placeholder="Search clients..." 
                  value={searchQuery}
                  onValueChange={handleSearch}
                />
                <CommandList>
                  <CommandEmpty>No clients found</CommandEmpty>
                  <CommandGroup heading="Recent">
                    {recentClients.slice(0, 5).map(client => (
                      <CommandItem
                        key={client.client_id}
                        onSelect={() => {
                          onSelectClient(entry.id, client);
                          setSearchOpen(false);
                          onPrefill(entry.id, client.client_id);
                        }}
                      >
                        <Clock className="h-3 w-3 mr-2 text-muted-foreground" />
                        {client.name}
                        {client.nick_name && <span className="ml-1 text-muted-foreground">({client.nick_name})</span>}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  {searchResults.length > 0 && (
                    <CommandGroup heading="Search Results">
                      {searchResults.map(client => (
                        <CommandItem
                          key={client.client_id}
                          onSelect={() => {
                            onSelectClient(entry.id, client);
                            setSearchOpen(false);
                            onPrefill(entry.id, client.client_id);
                          }}
                        >
                          <User className="h-3 w-3 mr-2" />
                          {client.name}
                          {client.nick_name && <span className="ml-1 text-muted-foreground">({client.nick_name})</span>}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Session (for session type) */}
        {entry.type === 'SESSION' && (
          <div>
            <Select 
              value={entry.session_id} 
              onValueChange={(v) => onUpdate(entry.id, 'session_id', v)}
            >
              <SelectTrigger className="h-8" data-testid={`session-select-${entry.id}`}>
                <SelectValue placeholder="Session" />
              </SelectTrigger>
              <SelectContent>
                {sessions.map(s => (
                  <SelectItem key={s.session_id} value={s.session_id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Rate */}
        <div>
          <Input
            type="number"
            step="0.01"
            placeholder="Rate"
            value={entry.rate}
            onChange={(e) => onUpdate(entry.id, 'rate', e.target.value)}
            className="h-8"
            data-testid={`rate-input-${entry.id}`}
          />
        </div>

        {/* Amount */}
        <div>
          <Input
            type="number"
            placeholder="Amount"
            value={entry.amount}
            onChange={(e) => onUpdate(entry.id, 'amount', e.target.value)}
            className="h-8"
            data-testid={`amount-input-${entry.id}`}
          />
        </div>

        {/* Mode */}
        <div>
          <Select 
            value={entry.mode} 
            onValueChange={(v) => onUpdate(entry.id, 'mode', v)}
          >
            <SelectTrigger className="h-8" data-testid={`mode-select-${entry.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {entry.type === 'MATCH' ? (
                <>
                  <SelectItem value="BACK">BACK</SelectItem>
                  <SelectItem value="LAY">LAY</SelectItem>
                </>
              ) : (
                <>
                  <SelectItem value="YES">YES</SelectItem>
                  <SelectItem value="NO">NO</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Team (for match) / Run (for session) */}
        {entry.type === 'MATCH' ? (
          <div>
            <Select 
              value={entry.team} 
              onValueChange={(v) => onUpdate(entry.id, 'team', v)}
            >
              <SelectTrigger className="h-8" data-testid={`team-select-${entry.id}`}>
                <SelectValue placeholder="Team" />
              </SelectTrigger>
              <SelectContent>
                {match && (
                  <>
                    <SelectItem value={match.team_a}>{match.team_a}</SelectItem>
                    <SelectItem value={match.team_b}>{match.team_b}</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div>
            <Input
              type="number"
              placeholder="Run"
              value={entry.run}
              onChange={(e) => onUpdate(entry.id, 'run', e.target.value)}
              className="h-8"
              data-testid={`run-input-${entry.id}`}
            />
          </div>
        )}
      </div>
    </div>
  );
}
